<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import { useUserStore, useCommonStore } from '@/shared/store'
  import { CountryBoundType, type Country } from '@/shared/types/api.types'
  import {
    usePerformanceTiming,
    useValidation,
    useValidators,
    useLinkTarget,
  } from '@/shared/composables'
  import {
    FLOW_RESIDENCE_WIDGET_QC_TEST_ID,
    PROCEED_EVENT_NAME,
    RESIDENCE_CARD_WARNING_TEST_ID,
    RESIDENCE_PRIORITY_IDS,
    RESIDENCE_INPUT_TEST_ID,
    VISITORS_LINK_TEST_ID,
  } from './FlowResidenceWidgetQC.constants'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { TravelDirection, type Locale } from '@/shared/types/common.types'
  import { AnalyticsPageName } from '@/shared/constants/analytics.constants'
  import { useFindCountries } from '@/shared/composables/useFindCountries'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import SelectQC from '@qicgroupdoha/qic-connect-ds/components/SelectQC'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import CountryFlagQC from '@qicgroupdoha/qic-connect-ds/components/CountryFlagQC'

  interface Props {
    withBackButton?: boolean
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (e: 'load', request: () => Promise<any>, requestId?: string, withLoader?: boolean): void
    (e: 'back'): void
  }

  withDefaults(defineProps<Props>(), {
    withBackButton: false,
  })

  const emit = defineEmits<Emits>()

  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { locale } = useI18n()
  const { residenceCountryValidator } = useValidators()
  const gaService = useGoogleAnalytics()
  const { getLinkTarget } = useLinkTarget()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const { findCountries, sortCountries } = useFindCountries()
  const selectedOption = ref(userStore.residenceCountry?.id ?? null)
  const listOfCountries = ref<Country[]>([])
  const isLoading = ref(false)

  const validationRules = {
    selectedOption: {
      residenceCountryValidator,
    },
  }

  const { v$, errors } = useValidation(validationRules, { selectedOption })

  const onProceed = async () => {
    if (listOfCountries.value.length === 0) return

    v$.value.$touch()

    if (!v$.value.$error) {
      const country = listOfCountries.value.find((country) => country.id === selectedOption.value)

      if (!country) return

      userStore.setResidenceCountry(
        {
          id: country.id,
          name: country.name,
        },
        true
      )

      emit(
        'load',
        async () => {
          isLoading.value = true
          await commonStore.sendFormData({
            originCode: country.id,
            originName: country.name,
          })

          gaService.sendSelectCountryProceed(country.name)
          isLoading.value = false

          emit(PROCEED_EVENT_NAME)
        },
        'sendFormData',
        false
      )
    }
  }

  onMounted(async () => {
    emit(
      'load',
      async () => {
        const countries = await userStore.fetchCountries(
          CountryBoundType.INBUND,
          locale.value as Locale
        )
        listOfCountries.value = sortCountries(countries, RESIDENCE_PRIORITY_IDS).map((country) => ({
          iso: country.isocode,
          ...country,
        }))

        onPerformanceCheckEnd()
        gaService.sendViewPage(TravelDirection.INBOUND, AnalyticsPageName.RESIDENCE)
      },
      'fetchCountries',
      false
    )
  })

  const visitorsHref = `${window.location.origin}/${locale.value}/visitors`
</script>

<template>
  <WrapperQC
    direction="column"
    class="flow-residence-widget-qc"
    :data-test="FLOW_RESIDENCE_WIDGET_QC_TEST_ID"
  >
    <FlowCardQC
      :title="$t('inbound_from_header')"
      with-button
      :is-loading="isLoading"
      :with-back-button="withBackButton"
      @button-click="onProceed"
      @back="emit('back')"
    >
      <SelectQC
        v-model="selectedOption"
        :label="$t('home_form_inbound_destination_label')"
        :locale="locale as Locale"
        title-key="name"
        value-key="id"
        :errors="errors.selectedOption"
        name="residenceCountry"
        :loading="!listOfCountries?.length || isLoading"
        :filter-function="(search: string) => findCountries(search, listOfCountries)"
        :container-attrs="{
          'data-test': RESIDENCE_INPUT_TEST_ID,
        }"
        :options="listOfCountries"
        is-filterable
        data-test="RESIDENCE_INPUT_TEST_ID"
      >
        <template #option-before-text="{ option }">
          <CountryFlagQC v-if="option.iso && typeof option.iso === 'string'" :iso="option.iso" />
        </template>
      </SelectQC>
    </FlowCardQC>

    <FlowCardQC class="residence-card-warning" :data-test="RESIDENCE_CARD_WARNING_TEST_ID">
      <WrapperQC gap="var(--spacing-4)" class="residence-card-warning__wrapper">
        <IconQC name="alert" color="var(--colorIcnSrfWarningYellow)" />
        <p class="residence-card-warning__text text-medium">
          {{ $t('inbound.country_of_residence.mandatory:insurance_wordings.start_new') }}
          <a
            class="residence-card-warning__link"
            :href="visitorsHref"
            :target="getLinkTarget()"
            :data-test="VISITORS_LINK_TEST_ID"
            @click="gaService.sendToVisitors()"
          >
            {{ $t('inbound.country_of_residence.mandatory:insurance_wordings.link_new') }}
          </a>
        </p>
      </WrapperQC>
    </FlowCardQC>
  </WrapperQC>
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/utils';

  .flow-residence-widget-qc {
    gap: var(--spacing-6);

    @include utils.mobile-flow {
      gap: var(--spacing-5);
    }
  }

  .residence-card-warning {
    padding: var(--spacing-6);
  }

  .residence-card-warning__wrapper {
    border-radius: var(--radius-5);
    background: var(--colorBgSrfWarningYellow);
    padding: var(--spacing-5);
    color: var(--colorTxtSrfWarningYellow);
  }

  .residence-card-warning__link {
    color: var(--colorTxtSrfWarningYellow);
    text-decoration: underline;
    transition: all 0.3s ease;

    &:hover,
    &:active {
      text-decoration-color: transparent;
    }
  }
</style>
